<template>
  <div class="promotion-create-form">
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2"
        cols="md-12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="md-12">
            <div class="d-inline-block">
              <h1 class="custom-header-title float-left pr-1 mb-0">
                {{ isDuplicate ? `Duplicating ${sourceName} - Copy` : 'Create new Promotion and Discount' }}
              </h1>
              <p
                v-if="isDuplicate"
                class="log_info"
              >
                Created by {{ sourceCreatedBy }} on {{ sourceCreatedAt }} <br>Last updated on {{ sourceUpdatedAt }}
              </p>
            </div>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <validation-observer
      ref="promotionCreateForm"
      #default="{ invalid }"
    >
      <b-form @submit.prevent="submitForm">
        <b-card header-tag="header">
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="GridIcon"
                class=""
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Promotions and Discount Details
            </h3>
          </template>
          <b-row>
            <b-col cols="md-6">
              <validation-provider
                #default="{ errors }"
                name="Name"
                vid="name"
                rules="required"
              >
                <b-form-group
                  label="Name*"
                  label-for="promotion-name"
                  label-cols-md="4"
                  :state="errors.length > 0 || nameValidation ? false : null"
                >
                  <b-form-input
                    id="promotion-name"
                    v-model="name"
                    placeholder="Enter a name for the promotion"
                    :state="errors.length > 0 || nameValidation ? false : null"
                    @input="nameValidation = false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="nameValidation"
                    class="text-danger"
                  >{{ nameError }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="md-6">
              <validation-provider
                #default="{ errors }"
                name="Label for Agent/Customer"
                vid="label"
                rules="required"
              >
                <b-form-group
                  label="Label for Agent/Customer*"
                  label-for="promotion-label"
                  label-cols-md="4"
                  :state="errors.length > 0 || labelValidation ? false : null"
                >
                  <b-form-input
                    id="promotion-label"
                    v-model="label"
                    placeholder="Enter a label shown to agents/customers"
                    :state="errors.length > 0 || labelValidation ? false : null"
                    @input="labelValidation = false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="labelValidation"
                    class="text-danger"
                  >{{ labelError }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="md-6">
              <validation-provider
                #default="{ errors }"
                name="Type of Promotion"
                vid="type"
                rules="required"
              >
                <b-form-group
                  label="Type of Promotion*"
                  label-for="promotion-type"
                  label-cols-md="4"
                  :state="errors.length > 0 || typeValidation ? false : null"
                >
                  <v-select
                    id="promotion-type"
                    v-model="type"
                    label="title"
                    placeholder="Select from list"
                    :options="typeOptions"
                    :reduce="title => title.code"
                    :clearable="false"
                    @input="typeValidation = false; couponCodeValidation = false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="typeValidation"
                    class="text-danger"
                  >{{ typeError }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Coupon Code*"
                label-for="promotion-coupon-code"
                label-cols-md="4"
                :state="couponCodeValidation ? false : null"
              >
                <b-input-group>
                  <b-form-input
                    id="promotion-coupon-code"
                    v-model="couponCode"
                    placeholder="Auto-generated or enter manually"
                    :disabled="type !== 'Coupon code'"
                    :state="couponCodeValidation ? false : null"
                    @input="couponCodeValidation = false"
                  />
                  <b-input-group-append is-text>
                    <span
                      class="text-primary cursor-pointer"
                      :class="{ disabled: type !== 'Coupon code' || generatingCode }"
                      @click="generateCouponCode"
                    >
                      Generate
                    </span>
                  </b-input-group-append>
                </b-input-group>
                <small
                  v-if="couponCodeValidation"
                  class="text-danger"
                >{{ couponCodeError }}</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Total Quota"
                label-for="promotion-total-quota"
                label-cols-md="4"
              >
                <b-form-input
                  id="promotion-total-quota"
                  v-model="totalQuota"
                  type="number"
                  min="0"
                  placeholder="Leave empty for no limit"
                />
                <small class="text-muted">Leave empty for no limit</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Quota per Agent"
                label-for="promotion-quota-per-agent"
                label-cols-md="4"
              >
                <b-form-input
                  id="promotion-quota-per-agent"
                  v-model="quotaPerAgent"
                  type="number"
                  min="0"
                  placeholder="Leave empty for no limit"
                />
                <small class="text-muted">Leave empty for no limit</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Validity - Start Date"
                label-for="promotion-validity-start"
                label-cols-md="4"
              >
                <b-input-group>
                  <flat-pickr
                    id="promotion-validity-start"
                    v-model="validityStartDate"
                    class="form-control flat-pickr-group"
                    placeholder="Choose a date"
                    :config="flatPickrConfig"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      size="18"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-muted">Leave empty if promotion is immediately applicable.</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Validity - End Date"
                label-for="promotion-validity-end"
                label-cols-md="4"
              >
                <b-input-group>
                  <flat-pickr
                    id="promotion-validity-end"
                    v-model="validityEndDate"
                    class="form-control flat-pickr-group"
                    placeholder="Choose a date"
                    :config="flatPickrConfig"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      size="18"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-muted">Leave empty if promotion does not expire.</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Applicable Agent(s)"
                label-for="promotion-applicable-agents"
                label-cols-md="4"
              >
                <v-select
                  id="promotion-applicable-agents"
                  v-model="applicableAgents"
                  label="label"
                  placeholder="Choose Agent Name, or Code"
                  multiple
                  :options="agentOptions"
                  :reduce="agent => agent._id"
                />
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Applicable Suite(s)"
                label-for="promotion-applicable-suites"
                label-cols-md="4"
              >
                <v-select
                  id="promotion-applicable-suites"
                  v-model="applicableSuites"
                  label="label"
                  placeholder="Choose Suite(s)"
                  multiple
                  :options="suiteOptions"
                  :reduce="suite => suite._id"
                />
                <small class="text-muted">Leave empty if promotion is usable for all suites.</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Applicable Products*"
                label-cols-md="4"
                :state="applicableProductsValidation ? false : null"
              >
                <div
                  v-for="product in displayProductOptions"
                  :key="product.name"
                >
                  <b-form-checkbox
                    v-model="applicableProducts"
                    :value="product.name"
                    @change="onProductToggle(product)"
                  >
                    {{ product.name }}
                  </b-form-checkbox>

                  <div
                    v-if="product.subProducts && product.subProducts.length"
                    class="pl-2"
                  >
                    <b-form-checkbox
                      v-for="subProduct in product.subProducts"
                      :key="subProduct.code"
                      v-model="applicableLotTypes"
                      :value="subProduct.code"
                      @change="onLotTypeToggle"
                    >
                      {{ subProduct.title }}
                    </b-form-checkbox>
                  </div>
                </div>
                <small
                  v-if="applicableProductsValidation"
                  class="text-danger"
                >{{ applicableProductsError }}</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Applicable Purchase Option(s)*"
                label-cols-md="4"
                :state="applicablePurchaseOptionsValidation ? false : null"
              >
                <b-form-checkbox
                  v-for="option in purchaseOptionOptions"
                  :key="option"
                  v-model="applicablePurchaseOptions"
                  :value="option"
                  @change="applicablePurchaseOptionsValidation = false"
                >
                  {{ option }}
                </b-form-checkbox>
                <small
                  v-if="applicablePurchaseOptionsValidation"
                  class="text-danger"
                >{{ applicablePurchaseOptionsError }}</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Required BD Verification?*"
                label-cols-md="4"
                :state="requiredBdVerificationValidation ? false : null"
              >
                <b-form-radio-group
                  v-model="requiredBdVerification"
                  :options="[{ text: 'Yes', value: true }, { text: 'No', value: false }]"
                  @change="requiredBdVerificationValidation = false"
                />
                <small
                  v-if="requiredBdVerificationValidation"
                  class="text-danger"
                >{{ requiredBdVerificationError }}</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6" />

            <b-col cols="md-6">
              <validation-provider
                #default="{ errors }"
                name="Priority"
                vid="priority"
                rules="required|integer|between:1,999"
              >
                <b-form-group
                  label="Priority*"
                  label-for="promotion-priority"
                  label-cols-md="4"
                  :state="errors.length > 0 || priorityValidation ? false : null"
                >
                  <b-form-input
                    id="promotion-priority"
                    v-model="priority"
                    type="number"
                    min="1"
                    max="999"
                    :state="errors.length > 0 || priorityValidation ? false : null"
                    @input="priorityValidation = false"
                  />
                  <small class="text-muted">Lower number = Higher priority (3 digits max)</small>
                  <br>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="priorityValidation"
                    class="text-danger"
                  >{{ priorityError }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="md-6">
              <validation-provider
                #default="{ errors }"
                name="Status"
                vid="status"
                rules="required"
              >
                <b-form-group
                  label="Status*"
                  label-for="promotion-status"
                  label-cols-md="4"
                  :state="errors.length > 0 || statusValidation ? false : null"
                >
                  <v-select
                    id="promotion-status"
                    v-model="status"
                    label="title"
                    placeholder="Select from list"
                    :options="statusOptions"
                    :reduce="title => title.code"
                    :clearable="false"
                    @input="statusValidation = false"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="statusValidation"
                    class="text-danger"
                  >{{ statusError }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </b-card>

        <b-card header-tag="header">
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="GridIcon"
                class=""
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Value Settings
            </h3>
          </template>
          <b-row>
            <b-col cols="md-6">
              <b-form-group
                label="Min. Purchase Value"
                label-for="promotion-min-purchase-value"
                label-cols-md="4"
              >
                <b-input-group
                  prepend="$"
                  class="input-group-merge"
                >
                  <b-form-input
                    id="promotion-min-purchase-value"
                    v-model="minPurchaseValue"
                    type="number"
                    min="0"
                  />
                </b-input-group>
                <small class="text-muted">Before promotion is applicable, excluding GST.</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Max. Purchase Value"
                label-for="promotion-max-purchase-value"
                label-cols-md="4"
              >
                <b-input-group
                  prepend="$"
                  class="input-group-merge"
                >
                  <b-form-input
                    id="promotion-max-purchase-value"
                    v-model="maxPurchaseValue"
                    type="number"
                    min="0"
                  />
                </b-input-group>
                <small class="text-muted">Before promotion is not applicable, excluding GST.</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <validation-provider
                #default="{ errors }"
                name="Type of Discount"
                vid="discountType"
                rules="required"
              >
                <b-form-group
                  label="Type of Discount*"
                  label-for="promotion-discount-type"
                  label-cols-md="4"
                  :state="errors.length > 0 || discountTypeValidation ? false : null"
                >
                  <v-select
                    id="promotion-discount-type"
                    v-model="discountType"
                    label="title"
                    placeholder="Select from list"
                    :options="discountTypeOptions"
                    :reduce="title => title.code"
                    :clearable="false"
                    @input="onDiscountTypeChange"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="discountTypeValidation"
                    class="text-danger"
                  >{{ discountTypeError }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Discount Value*"
                label-for="promotion-discount-value"
                label-cols-md="4"
                :state="discountValueValidation ? false : null"
              >
                <b-input-group
                  :prepend="discountType === 'Percentage (%)' ? undefined : '$'"
                  :append="discountType === 'Percentage (%)' ? '%' : undefined"
                  class="input-group-merge"
                >
                  <b-form-input
                    id="promotion-discount-value"
                    v-model="discountValue"
                    type="number"
                    min="0"
                    :disabled="isEntitlementsDiscount"
                    :state="discountValueValidation ? false : null"
                    @input="discountValueValidation = false"
                  />
                </b-input-group>
                <small
                  v-if="discountValueValidation"
                  class="text-danger"
                >{{ discountValueError }}</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Transaction Code*"
                label-for="promotion-transaction-code"
                label-cols-md="4"
                :state="transactionCodeValidation ? false : null"
              >
                <v-select
                  id="promotion-transaction-code"
                  v-model="transactionCode"
                  label="label"
                  placeholder="Select from list"
                  :options="transactionCodeOptions"
                  :reduce="item => item.code"
                  :disabled="isEntitlementsDiscount"
                  @input="transactionCodeValidation = false"
                />
                <small
                  v-if="transactionCodeValidation"
                  class="text-danger"
                >{{ transactionCodeError }}</small>
              </b-form-group>
            </b-col>

            <b-col cols="md-6">
              <b-form-group
                label="Max Discount Value"
                label-for="promotion-max-discount-value"
                label-cols-md="4"
              >
                <b-input-group
                  prepend="$"
                  class="input-group-merge"
                >
                  <b-form-input
                    id="promotion-max-discount-value"
                    v-model="maxDiscountValue"
                    type="number"
                    min="0"
                    :disabled="isEntitlementsDiscount"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-nav class="wrap-border save-nav">
          <li class="nav-item ml-auto">
            <b-button
              type="button"
              variant="flat-primary"
              :to="{ name: 'products-promotions-index' }"
            >
              Cancel
            </b-button>
          </li>
          <li class="nav-item mr-1">
            <b-button
              type="submit"
              variant="success"
              :disabled="invalid"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-50"
              />
              <span class="align-middle">Create Promotion and Discount</span>
            </b-button>
          </li>
        </b-nav>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BButton, BForm, BFormGroup, BFormInput, BFormCheckbox, BFormRadioGroup,
  BInputGroup, BInputGroupAppend, BNav,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import '@core/scss/vue/libs/vue-flatpicker.scss'
import { required, integer, between } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormRadioGroup,
    BInputGroup,
    BInputGroupAppend,
    BNav,
    vSelect,
    flatPickr,

    ValidationProvider,
    ValidationObserver,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  directives: { Ripple },
  data() {
    return {
      isDuplicate: false,
      sourceName: '',
      sourceCreatedBy: '',
      sourceCreatedAt: '',
      sourceUpdatedAt: '',

      name: '',
      type: null,
      label: '',
      couponCode: '',
      generatingCode: false,
      totalQuota: null,
      quotaPerAgent: null,
      validityStartDate: '',
      validityEndDate: '',
      priority: 99,
      applicableAgents: [],
      applicableSuites: [],
      applicableProducts: [],
      applicableLotTypes: [],
      applicablePurchaseOptions: [],
      requiredBdVerification: null,
      status: 'Active',
      minPurchaseValue: null,
      maxPurchaseValue: null,
      discountType: null,
      discountValue: null,
      maxDiscountValue: null,
      transactionCode: null,

      typeOptions: [
        { title: 'Auto apply', code: 'Auto apply' },
        { title: 'Coupon code', code: 'Coupon code' },
      ],
      statusOptions: [
        { title: 'Active', code: 'Active' },
        { title: 'Inactive', code: 'Inactive' },
      ],
      discountTypeOptions: [
        { title: 'Singapore Dollars ($)', code: 'Singapore Dollars ($)' },
        { title: 'Percentage (%)', code: 'Percentage (%)' },
        { title: 'N/A - For Entitlements Use', code: 'N/A - For Entitlements Use' },
      ],
      purchaseOptionOptions: ['Book for 10 days', 'Pay downpayment', 'Pay in full'],
      productDisplayOrder: ['Niche', 'Pedestal', 'Urn', 'Little Buddha', 'Funeral Service Package'],
      lotTypeDisplayOrder: ['SINGLE', 'DOUBLE', 'FAMILY', 'SUPER FAMILY', 'TWIN SINGLE', 'TWIN DOUBLE', 'MANSIONETTE TWIN SINGLE', 'MANSIONETTE TWIN DOUBLE'],
      productOptions: [],
      transactionCodeOptions: [],
      agentOptions: [],
      suiteOptions: [],

      flatPickrConfig: {
        wrap: true, enableTime: false, dateFormat: 'd/m/Y', disableMobile: true,
      },

      nameValidation: false,
      nameError: '',
      labelValidation: false,
      labelError: '',
      typeValidation: false,
      typeError: '',
      couponCodeValidation: false,
      couponCodeError: '',
      priorityValidation: false,
      priorityError: '',
      applicableProductsValidation: false,
      applicableProductsError: 'At least one Applicable Product is required',
      applicablePurchaseOptionsValidation: false,
      applicablePurchaseOptionsError: 'At least one Applicable Purchase Option is required',
      requiredBdVerificationValidation: false,
      requiredBdVerificationError: 'This field is required',
      statusValidation: false,
      statusError: '',
      discountTypeValidation: false,
      discountTypeError: '',
      discountValueValidation: false,
      discountValueError: 'Discount Value is required',
      transactionCodeValidation: false,
      transactionCodeError: 'Transaction Code is required',

      required,
      integer,
      between,
    }
  },
  computed: {
    isEntitlementsDiscount() {
      return this.discountType === 'N/A - For Entitlements Use'
    },
    displayProductOptions() {
      return this.productDisplayOrder
        .map(name => this.productOptions.find(product => product.name === name))
        .filter(Boolean)
        .map(product => ({
          ...product,
          subProducts: product.subProducts && product.subProducts.length
            ? this.lotTypeDisplayOrder
              .map(code => product.subProducts.find(subProduct => subProduct.code === code))
              .filter(Boolean)
            : [],
        }))
    },
  },
  watch: {
    applicableProducts(value) {
      if (!value.includes('Niche')) {
        this.applicableLotTypes = []
      }
    },
  },
  mounted() {
    this.$http.get('reservation/promotions/respond-with/products')
      .then(response => {
        this.productOptions = response.data.products ?? []
      })
      .catch(() => {})

    this.$http.get('reservation/promotions/respond-with/transaction-codes')
      .then(response => {
        this.transactionCodeOptions = response.data.transactionCodes ?? []
      })
      .catch(() => {})

    this.$http.get('reservation/reservations/respond-with/agents')
      .then(response => {
        this.agentOptions = (response.data.agents ?? [])
          .filter(agent => agent.modelType === 'Agent')
          .map(agent => ({ _id: agent._id, label: `${agent.name} (${agent.code})` }))
      })
      .catch(() => {})

    this.$http.get('reservation/suites/respond-with/name-and-id')
      .then(response => {
        this.suiteOptions = (response.data.suites ?? [])
          .map(suite => ({ _id: suite._id, label: `Suite ${suite.code}` }))
      })
      .catch(() => {})

    if (this.$route.query.from) {
      this.isDuplicate = true
      this.$http.get(`reservation/promotions/${this.$route.query.from}/show`)
        .then(response => {
          const source = response.data.data

          this.sourceName = source.name
          this.sourceCreatedBy = source.createdBy ? source.createdBy.name : '-'
          this.sourceCreatedAt = source.createdAt ? this.dateFormatWithTime(source.createdAt) : '-'
          this.sourceUpdatedAt = source.updatedAt ? this.dateFormatWithTime(source.updatedAt) : '-'

          this.name = source.name
          this.type = source.type
          this.label = source.label
          this.couponCode = ''
          this.totalQuota = source.totalQuota
          this.quotaPerAgent = source.quotaPerAgent
          this.validityStartDate = source.validityStartDate ? this.dateFormat(source.validityStartDate) : ''
          this.validityEndDate = source.validityEndDate ? this.dateFormat(source.validityEndDate) : ''
          this.priority = source.priority
          this.applicableAgents = (source.applicableAgents || []).map(agent => agent._id)
          this.applicableSuites = (source.applicableSuites || []).map(suite => suite._id)
          this.applicableProducts = source.applicableProducts || []
          this.applicableLotTypes = source.applicableLotTypes || []
          this.applicablePurchaseOptions = source.applicablePurchaseOptions || []
          this.requiredBdVerification = source.requiredBdVerification
          this.status = source.status
          this.minPurchaseValue = source.minPurchaseValue
          this.maxPurchaseValue = source.maxPurchaseValue
          this.discountType = source.discountType
          this.discountValue = source.discountValue
          this.maxDiscountValue = source.maxDiscountValue
          this.transactionCode = source.transactionCode
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }
  },
  methods: {
    onProductToggle(product) {
      this.applicableProductsValidation = false
      if (product.name === 'Niche' && !this.applicableProducts.includes('Niche')) {
        this.applicableLotTypes = []
      }
    },
    onLotTypeToggle() {
      if (this.applicableLotTypes.length && !this.applicableProducts.includes('Niche')) {
        this.applicableProducts.push('Niche')
      }
    },
    onDiscountTypeChange() {
      this.discountTypeValidation = false
      if (this.isEntitlementsDiscount) {
        this.discountValue = null
        this.transactionCode = null
        this.maxDiscountValue = null
        this.discountValueValidation = false
        this.transactionCodeValidation = false
      }
    },
    generateCouponCode() {
      if (this.type !== 'Coupon code' || this.generatingCode) return

      this.generatingCode = true
      this.$http.get('reservation/promotions/respond-with/generate-coupon-code')
        .then(response => {
          this.couponCode = response.data.couponCode
          this.couponCodeValidation = false
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.generatingCode = false
        })
    },
    validateConditionalFields() {
      let valid = true

      if (this.type === 'Coupon code' && !this.couponCode) {
        this.couponCodeValidation = true
        this.couponCodeError = 'Coupon Code is required'
        valid = false
      }

      if (!this.applicableProducts.length) {
        this.applicableProductsValidation = true
        valid = false
      }

      if (!this.applicablePurchaseOptions.length) {
        this.applicablePurchaseOptionsValidation = true
        valid = false
      }

      if (this.requiredBdVerification === null) {
        this.requiredBdVerificationValidation = true
        valid = false
      }

      if (!this.isEntitlementsDiscount) {
        if (this.discountValue === null || this.discountValue === '') {
          this.discountValueValidation = true
          valid = false
        }
        if (!this.transactionCode) {
          this.transactionCodeValidation = true
          valid = false
        }
      }

      return valid
    },
    submitForm() {
      this.$refs.promotionCreateForm.validate().then(success => {
        if (success && this.validateConditionalFields()) {
          const formData = new FormData()

          formData.append('name', this.name)
          formData.append('type', this.type)
          formData.append('label', this.label)
          formData.append('couponCode', this.couponCode || '')
          formData.append('totalQuota', this.totalQuota ?? '')
          formData.append('quotaPerAgent', this.quotaPerAgent ?? '')
          formData.append('validityStartDate', this.validityStartDate || '')
          formData.append('validityEndDate', this.validityEndDate || '')
          formData.append('priority', this.priority)
          formData.append('applicableAgents', JSON.stringify(this.applicableAgents))
          formData.append('applicableSuites', JSON.stringify(this.applicableSuites))
          formData.append('applicableProducts', JSON.stringify(this.applicableProducts))
          formData.append('applicableLotTypes', JSON.stringify(this.applicableLotTypes))
          formData.append('applicablePurchaseOptions', JSON.stringify(this.applicablePurchaseOptions))
          formData.append('requiredBdVerification', this.requiredBdVerification)
          formData.append('status', this.status)
          formData.append('minPurchaseValue', this.minPurchaseValue ?? '')
          formData.append('maxPurchaseValue', this.maxPurchaseValue ?? '')
          formData.append('discountType', this.discountType)
          formData.append('discountValue', this.discountValue ?? '')
          formData.append('maxDiscountValue', this.maxDiscountValue ?? '')
          formData.append('transactionCode', this.transactionCode || '')

          this.$http.post('reservation/promotions/store', formData)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Promotion has been created successfully.',
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })
              this.$router.push({ name: 'products-promotions-index' })
            })
            .catch(error => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              })
            })
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

// _calllog.scss leaks two unscoped rules onto every card-body input-group:
// `.input-group-text { border-left: none }` and, with !important,
// `.input-group .form-control { border-right: none }`. Restore both borders,
// and since the !important above also locks out bootstrap's own
// :focus-within border-color, restate the focus color here too.
.promotion-create-form .input-group-merge .input-group-prepend .input-group-text {
  border-left: 1px solid #d8d6de;
}
.promotion-create-form .input-group-merge .form-control {
  border-right: 1px solid #d8d6de !important;
}
.promotion-create-form .input-group-merge:focus-within .input-group-prepend .input-group-text,
.promotion-create-form .input-group-merge:focus-within .form-control {
  border-color: #7367f0 !important;
}
</style>
